import { useAuth } from "zudoku/components";
import { useZudoku } from "zudoku/hooks";
import { useState, useEffect } from "react";

interface TickerRow {
  ticker: string;
  times_called: number;
  created_at: string;
  updated_at: string;
}

interface UsageData {
  organization_id: string;
  billing_period_start?: string;
  billingPeriodStart?: string;
  billing_period_end?: string;
  billingPeriodEnd?: string;
  tickers: TickerRow[];
}

const USAGE_ENDPOINT = "https://api.securitydatapoint.com/api/v1/reports-usage-details";

function decodeJwt(token: string): Record<string, any> | null {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
  } catch {
    return null;
  }
}

function formatDate(iso: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatDateShort(iso: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export const TickerUsagePage = () => {
  const auth = useAuth();
  const { authentication } = useZudoku();
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const getAccessToken = async (): Promise<string | null> => {
    try {
      if (authentication?.getAccessToken) return await authentication.getAccessToken();
    } catch (e) {
      console.warn("getAccessToken() failed, falling back:", e);
    }
    return (
      (auth as any).providerData?.accessToken ||
      (auth as any).providerData?.access_token ||
      null
    );
  };

  const getUserData = () => {
    const token =
      (auth as any).providerData?.accessToken ||
      (auth.user as any)?.access_token;
    if (!token) return { isSuperAdmin: false, userTier: "unknown" };
    const payload = decodeJwt(token);
    if (!payload) return { isSuperAdmin: false, userTier: "unknown" };
    const permission =
      payload["https://api.securitydatapoint.com/permission"] ||
      payload.permission ||
      "";
    const userTier =
      payload["https://api.securitydatapoint.com/tier"] ||
      payload.tier ||
      "unknown";
    return { isSuperAdmin: permission === "superadmin", userTier };
  };

  const { isSuperAdmin, userTier } = auth.isAuthenticated
    ? getUserData()
    : { isSuperAdmin: false, userTier: "unknown" };

  const hasAccess = isSuperAdmin && (userTier === "scale" || userTier === "enterprise");

  useEffect(() => {
    if (!auth.isAuthenticated || !hasAccess) return;

    const fetchUsage = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const token = await getAccessToken();
        if (!token) {
          setLoadError("Unable to retrieve access token.");
          return;
        }
        const response = await fetch(USAGE_ENDPOINT, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          setLoadError(body?.error ?? `Request failed (${response.status})`);
          return;
        }
        setData(await response.json());
      } catch (e: any) {
        setLoadError(e?.message ?? "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchUsage();
  }, [auth.isAuthenticated, hasAccess]);

  if (!auth.isAuthenticated) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <h1>Subscription Usage</h1>
        <p>Please log in to access this page.</p>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <h1>Subscription Usage</h1>
        <p style={{ color: "#dc2626", marginTop: "20px" }}>
          Access denied. This page is only available for superadmins on the Scale or Enterprise tier.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: "40px", maxWidth: "1200px", margin: "0 auto" }}>
      <h1 style={{ marginBottom: "6px" }}>Subscription Usage</h1>
      <p style={{ color: "#6b7280", marginBottom: "8px" }}>
        Per-ticker request counts for your organization in the current billing period (for both API and Portal requests).
      </p>

      {data && (
        <div style={{ marginBottom: "24px", color: "#374151", fontSize: "14px" }}>
          <span>
            Billing period:{" "}
            <strong>{formatDateShort(data.billing_period_start || data.billingPeriodStart || "")}</strong>
            {" — "}
            <strong>{formatDateShort(data.billing_period_end || data.billingPeriodEnd || "")}</strong>
          </span>
          <span style={{ marginLeft: "24px" }}>
            Resets on: <strong>{formatDateShort(data.billing_period_end || data.billingPeriodEnd || "")}</strong>
          </span>
        </div>
      )}

      {loading && (
        <p style={{ color: "#6b7280" }}>Loading Subscription Usage…</p>
      )}

      {loadError && (
        <p style={{ color: "#dc2626" }}>Error: {loadError}</p>
      )}

      {!loading && !loadError && data && data.tickers.length === 0 && (
        <p style={{ color: "#6b7280" }}>No tickers used in this billing period yet.</p>
      )}

      {!loading && !loadError && data && data.tickers.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
              backgroundColor: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                {[
                  "Ticker",
                  "Times Called",
                  "First Called",
                  "Last Called",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 600,
                      color: "#374151",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.tickers.map((row, idx) => (
                <tr
                  key={row.ticker}
                  style={{
                    borderBottom: "1px solid #e5e7eb",
                    backgroundColor: idx % 2 === 0 ? "#fff" : "#f9fafb",
                  }}
                >
                  <td
                    style={{
                      padding: "10px 16px",
                      fontWeight: 600,
                      fontFamily: "monospace",
                      color: "#111827",
                    }}
                  >
                    {row.ticker}
                  </td>
                  <td style={{ padding: "10px 16px", color: "#374151" }}>
                    {row.times_called.toLocaleString()}
                  </td>
                  <td style={{ padding: "10px 16px", color: "#6b7280", whiteSpace: "nowrap" }}>
                    {formatDate(row.created_at)}
                  </td>
                  <td style={{ padding: "10px 16px", color: "#6b7280", whiteSpace: "nowrap" }}>
                    {formatDate(row.updated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ marginTop: "12px", color: "#9ca3af", fontSize: "13px" }}>
            {data.tickers.length} ticker{data.tickers.length !== 1 ? "s" : ""} used this period
            {" - "}your organization: {data.organization_id}
          </p>
        </div>
      )}
    </div>
  );
};
